/**
 * Layout principal autenticado: drawer DaisyUI 5 (sidebar siempre visible
 * en lg+, hamburger drawer en mobile/tablet) + header glass + outlet.
 */
import { useState } from "react";
import { Link, Outlet, useLocation } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  LayoutDashboard,
  FolderKanban,
  ClipboardList,
  CalendarDays,
  CalendarRange,
  Eye,
  Ticket as TicketIcon,
  Users2,
  UsersRound,
  Settings2,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import clsx from "clsx";
import { api } from "@/lib/api";
import type { SessionUser } from "@axorial/shared";
import { ThemeToggle } from "@/components/ThemeToggle";
import { PageTitleProvider, usePageTitleSlots } from "@/lib/page-title";

type NavItem = {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  requiresRole?: readonly ("admin" | "finanzas" | "lead" | "member")[];
};

const NAV: NavItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/projects", label: "Proyectos", icon: FolderKanban },
  { to: "/standups", label: "Standups", icon: ClipboardList },
  { to: "/days-off", label: "Días libres", icon: CalendarDays },
  { to: "/tickets", label: "Tickets", icon: TicketIcon },
  { to: "/calendar", label: "Calendario", icon: CalendarRange },
  { to: "/sesiones", label: "Sesiones", icon: ClipboardList },
  { to: "/team", label: "Compañeros", icon: UsersRound },
  // Sección "Evaluaciones" pausada — ver EVALUATIONS_PAUSED.md. La página
  // sigue funcional (KPIs/Cuestionarios/Histórico) si se entra por URL.
  // { to: "/evaluations", label: "Evaluaciones", icon: LineChart },
  // Finanzas vive en fin.axorial.mx (worker + DB aparte). Acceso solo
  // por URL directa — no exponemos shortcut acá para mantener la nav
  // del panel limpia y centrada en operación.
  { to: "/crm", label: "CRM", icon: Users2, requiresRole: ["admin", "lead"] as const },
  { to: "/admin", label: "Administración", icon: Settings2, requiresRole: ["admin"] as const },
];

export function AppShell() {
  return (
    <PageTitleProvider>
      <AppShellInner />
    </PageTitleProvider>
  );
}

function AppShellInner() {
  const { setTitleEl, setActionsEl, pageHeaderCount } = usePageTitleSlots();
  const qc = useQueryClient();
  const { data: me } = useQuery<SessionUser>({
    queryKey: ["me"],
    queryFn: () => api.get("/me"),
  });
  const stopImpersonating = useMutation<{ ok: true }>({
    mutationFn: () => api.del("/admin/impersonate"),
    onSuccess: () => {
      qc.clear();
      window.location.href = "/";
    },
  });
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Sidebar colapsado en desktop. Persiste en localStorage para no
  // perder la preferencia entre sesiones.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("axorial.sidebar.collapsed") === "1";
  });
  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      if (typeof window !== "undefined") {
        localStorage.setItem("axorial.sidebar.collapsed", next ? "1" : "0");
      }
      return next;
    });
  };

  const activeItem = NAV.find((n) =>
    n.to === "/" ? location.pathname === "/" : location.pathname.startsWith(n.to),
  );

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <div
      className={clsx("drawer lg:drawer-open", collapsed && "lg:[--drawer-side-width:4rem]")}
      style={collapsed ? ({ "--drawer-side-width": "4rem" } as React.CSSProperties) : undefined}
    >
      <input
        id="axorial-drawer"
        type="checkbox"
        className="drawer-toggle"
        checked={drawerOpen}
        onChange={(e) => setDrawerOpen(e.target.checked)}
      />

      {/* ───── Contenido principal ───── */}
      <div className="drawer-content flex flex-col min-h-screen bg-base-200">
        <header className="min-h-14 px-4 md:px-6 py-2 border-b border-base-300 glass-panel flex items-center justify-between gap-3 sticky top-0 z-10">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <label
              htmlFor="axorial-drawer"
              className="btn btn-ghost btn-sm btn-square lg:hidden -ml-2 shrink-0"
              aria-label="Abrir menú"
            >
              <Menu className="size-5" />
            </label>
            {/* Slot del PageHeader. `display:contents` hace que el div
                contenedor desaparezca del box tree y el wrapper inyectado
                por <PageHeader/> participe directo del flex padre.
                Mientras ninguna ruta esté inyectando (pageHeaderCount===0),
                mostramos el label del nav como fallback. */}
            <div ref={setTitleEl} className="contents" />
            {pageHeaderCount === 0 && (
              <h1 className="font-display font-semibold text-base-content tracking-tight truncate">
                {activeItem?.label ?? ""}
              </h1>
            )}
          </div>
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            {/* Slot de actions del PageHeader (botones de la página actual). */}
            <div ref={setActionsEl} className="contents" />
            <div className="text-xs sm:text-sm opacity-60 capitalize hidden sm:block">
              {new Date().toLocaleDateString("es-MX", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            </div>
            <div className="text-xs opacity-60 capitalize sm:hidden">
              {new Date().toLocaleDateString("es-MX", {
                day: "numeric",
                month: "short",
              })}
            </div>
            <ThemeToggle />
          </div>
        </header>

        {/* Banner de impersonación — sticky bajo el header. Toda la app
            queda con un borde amarillo cuando estás viendo como otro
            usuario, para que sea imposible no notarlo. */}
        {me?.impersonatedBy && (
          <div className="sticky top-14 z-10 bg-warning text-warning-content border-b-2 border-warning-content/30 px-4 md:px-6 py-2.5 flex items-center gap-3 text-sm flex-wrap">
            <Eye className="size-4 shrink-0" />
            <div className="flex-1 min-w-0 basis-full sm:basis-auto">
              <span className="font-semibold">Viendo como {me.name}</span>
              <span className="opacity-80 ml-2 hidden md:inline">
                ({me.email}) · sesión solo lectura · iniciada por {me.impersonatedBy.name}
              </span>
              <span className="opacity-80 ml-2 md:hidden">solo lectura</span>
            </div>
            <button
              type="button"
              onClick={() => stopImpersonating.mutate()}
              disabled={stopImpersonating.isPending}
              className="btn btn-sm bg-warning-content text-warning hover:bg-warning-content/90 border-warning-content"
            >
              {stopImpersonating.isPending && (
                <span className="loading loading-spinner loading-xs" />
              )}
              Volver a admin
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          {/* Ancho fluido en lg+ para aprovechar pantallas anchas; en
              pantallas muy anchas (2xl+) máximo 1800px para que el texto
              no quede tan extendido que sea incómodo de leer. */}
          <div className="mx-auto w-full 2xl:max-w-[1800px]">
            <Outlet />
          </div>
        </div>
      </div>

      {/* ───── Sidebar (drawer side) ───── */}
      <div className="drawer-side z-20">
        <label htmlFor="axorial-drawer" aria-label="Cerrar menú" className="drawer-overlay" />
        <aside
          className={clsx(
            "min-h-full sidebar-gradient text-white/90 flex flex-col relative transition-[width] duration-200",
            collapsed ? "w-16" : "w-64",
          )}
        >
          {/* Glow sutil al top del sidebar */}
          <div
            aria-hidden
            className="absolute inset-x-0 top-0 h-32 pointer-events-none opacity-50"
            style={{
              background:
                "radial-gradient(ellipse at top, oklch(72% 0.13 198 / 0.18) 0%, transparent 70%)",
            }}
          />

          <div
            className={clsx(
              "px-3 py-5 border-b border-white/8 relative flex items-center gap-2",
              collapsed ? "flex-col" : "justify-between",
            )}
          >
            <Link to="/" onClick={closeDrawer} className="flex items-center gap-3 group min-w-0">
              <img
                src="/logo/isotipo.png"
                alt="Axorial"
                className="size-10 rounded-xl bg-white/95 p-1 shadow-lg shadow-primary/20 group-hover:shadow-primary/40 transition-shadow shrink-0 object-contain"
              />
              {!collapsed && (
                <div className="leading-tight min-w-0">
                  <div className="font-semibold tracking-tight truncate">Axorial</div>
                  <div className="text-xs opacity-60 truncate">Panel interno</div>
                </div>
              )}
            </Link>
            {/* Toggle solo visible en lg+ (en mobile el drawer cierra con overlay) */}
            <button
              type="button"
              onClick={toggleCollapsed}
              className="hidden lg:grid size-7 place-items-center rounded-md hover:bg-white/10 transition-colors text-white/70 hover:text-white shrink-0"
              aria-label={collapsed ? "Expandir menú" : "Colapsar menú"}
              title={collapsed ? "Expandir menú" : "Colapsar menú"}
            >
              {collapsed ? (
                <PanelLeftOpen className="size-4" />
              ) : (
                <PanelLeftClose className="size-4" />
              )}
            </button>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto relative">
            {NAV.map((item) => {
              if (item.requiresRole && me && !item.requiresRole.includes(me.role as never)) {
                return null;
              }
              const active =
                item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to);
              const Icon = item.icon;
              return (
                <div key={item.to}>
                  <Link
                    to={item.to}
                    onClick={closeDrawer}
                    title={collapsed ? item.label : undefined}
                    className={clsx(
                      "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm relative group",
                      collapsed && "justify-center",
                      active
                        ? "bg-white/10 text-white font-medium"
                        : "opacity-75 hover:opacity-100 hover:bg-white/5",
                    )}
                  >
                    {active && !collapsed && (
                      <span
                        aria-hidden
                        className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary rounded-r-full shadow-[0_0_8px_var(--color-primary)]"
                      />
                    )}
                    <Icon
                      className={clsx(
                        "size-4 transition-colors shrink-0",
                        active ? "text-primary" : "text-current",
                      )}
                    />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </Link>
                </div>
              );
            })}
          </nav>

          <div className="p-3 border-t border-white/8 text-xs relative">
            {me ? (
              <div
                className={clsx("flex items-center gap-3", collapsed && "justify-center")}
                title={collapsed ? `${me.name} (${me.role})` : undefined}
              >
                <div className="size-9 rounded-full bg-white/10 grid place-items-center text-white font-semibold uppercase shrink-0">
                  {me.name
                    .split(" ")
                    .slice(0, 2)
                    .map((p) => p[0])
                    .join("")}
                </div>
                {!collapsed && (
                  <>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-white truncate">{me.name}</div>
                      <div className="opacity-60 truncate">{me.email}</div>
                    </div>
                    <span className="badge badge-primary badge-sm capitalize shrink-0">
                      {me.role}
                    </span>
                  </>
                )}
              </div>
            ) : (
              <span className="loading loading-spinner loading-xs" />
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
